import React, { useEffect, useState } from 'react';
import { Col, Row } from '@gjensidige/core-grid';
import { Text, Title } from '@gjensidige/core-typography';
import { useCmsContext } from '@gjensidige/service-cms';

import { MD_BREAKPOINT, TIMEOUT } from '../../data/constants';
import { useWindowSize } from '../../hooks/useWindowsSize';
import tracker from '../../tracking';
import { AppStep, ScenarioStep, Step } from '../../types/enums';
import { getLatestStepToGoToFromAnswers } from '../../utils';
import { useCheckListContext } from '../../context/CheckListProvider';
import { checkList } from '../../data/ChekListItems';
import { SecondaryButton } from '../Button';
import RadioButtons from '../RadioButtons';
import styles from './CheckWrapper.module.scss';
import Modal from './Modal';
import StepsDesktop from './StepsDesktop';
import StepsMobile from './StepsMobile';

const CheckWrapper = () => {
  const [showModal, setShowModal] = useState(false);
  const [showNextButton, setShowNextButton] = useState(false);
  const { getText } = useCmsContext();
  const {
    currentStep,
    setCurrentStep,
    answers,
    setAnswerByStep,
    setAppStep
  } = useCheckListContext();
  const { windowWidth } = useWindowSize();

  useEffect(() => {
    if (answers[currentStep] !== '') {
      setShowNextButton(true);
    } else {
      setShowNextButton(false);
    }
  }, [currentStep]);

  const toggleModal = () => {
    setShowModal((prevState) => !prevState);
  };

  const handleAnswerChange = (answer: string) => {
    setAnswerByStep(currentStep, answer);
    setTimeout(() => {
      moveToNextStep(currentStep);
    }, TIMEOUT);
  };

  const moveToCurrentStep = (step: Step) => {
    const latestStepToPossiblyGoTo: Step = getLatestStepToGoToFromAnswers(
      answers
    );

    if (step <= latestStepToPossiblyGoTo && step !== currentStep) {
      tracker.onScenarioStepChange((ScenarioStep as any)[Step[step]]);
      setCurrentStep(step);
    }
  };

  const moveToNextStep = (step: Step) => {
    const nextStep = checkList[step].next;
    if (nextStep !== null) {
      tracker.onScenarioStepChange((ScenarioStep as any)[Step[nextStep]]);
      setCurrentStep(nextStep);
    } else {
      tracker.onScenarioStepChange(ScenarioStep.SUMMARY);
      setAppStep(AppStep.SUMMARY);
    }
  };

  const moveToPreviousStep = (currentStep: Step) => {
    const previousStep = checkList[currentStep].back;
    if (previousStep !== null) {
      tracker.onScenarioStepChange((ScenarioStep as any)[Step[previousStep]]);
      setCurrentStep(previousStep);
    }
  };

  const renderContent = () => {
    const texts = checkList[currentStep].texts;
    const options = [
      {
        label: getText(`${texts}.option1`),
        value: getText(`${texts}.option1`)
      },
      {
        label: getText(`${texts}.option2`),
        value: getText(`${texts}.option2`)
      }
    ];

    return (
      <>
        <Row className={styles.textWrapper}>
          <Col>
            <Title tag="h2" size="h4">
              {getText(`${texts}.title`)}
            </Title>
            <Text> {getText(`${texts}.text`)}</Text>
          </Col>
        </Row>
        <SecondaryButton
          noPadding
          onClick={toggleModal}
          enableTracking={true}
          tracking={{
            eventLabel: `les-mer-om-hvordan-og-hvorfor_${
              tracker.getOptions().scenario_name
            }_${tracker.getOptions().scenario_step}`
          }}
        >
          {getText('housing.check.common.linkToModal')}
        </SecondaryButton>
        <Modal
          visible={showModal}
          toggle={toggleModal}
          checkListItem={checkList[currentStep]}
        />
        <Row>
          <Col>
            <RadioButtons options={options} onChange={handleAnswerChange} />
          </Col>
        </Row>
        <Row>
          <Col className={styles.buttonWrapper}>
            {checkList[currentStep].back !== null && (
              <SecondaryButton onClick={() => moveToPreviousStep(currentStep)}>
                {getText('housing.check.common.linkBack')}
              </SecondaryButton>
            )}
            {showNextButton && (
              <SecondaryButton onClick={() => moveToNextStep(currentStep)}>
                {getText('housing.check.common.linkForward')}
              </SecondaryButton>
            )}
          </Col>
        </Row>
      </>
    );
  };

  return (
    <>
      {windowWidth < MD_BREAKPOINT ? (
        <>
          <StepsMobile
            checkList={checkList}
            onNavigationClick={moveToCurrentStep}
          />
          {renderContent()}
        </>
      ) : (
        <StepsDesktop
          checkList={checkList}
          onNavigationClick={moveToCurrentStep}
        >
          {renderContent()}
        </StepsDesktop>
      )}
    </>
  );
};

export default CheckWrapper;
